/**
 * Katalog der Anlage- und Bearbeitungsformulare — woertlich aus dem Entwurf
 * dem HTML-Prototyp uebernommen (`FORMULARE`).
 *
 * Ein Muster fuer die ganze Anwendung: Feldgruppen, Pflichtfelder,
 * Formatpruefung, Eindeutigkeit (C0).
 *
 * Diese Datei wird nicht „verbessert". Aenderungen an Beschriftungen,
 * Reihenfolge oder Pruefmustern gehoeren zuerst in den Entwurf.
 */

import type { Modul } from './navigation';

export type Feldtyp = 'text' | 'auswahl' | 'fliess';

export interface Feld {
  /** Schluessel im Wertesatz. */
  k: string;
  label: string;
  typ?: Feldtyp;
  pflicht?: boolean;
  /** Ueber beide Spalten des Rasters. */
  breit?: boolean;
  platzhalter?: string;
  /** Regulaerer Ausdruck als Zeichenfolge, wie im Entwurf. */
  muster?: string;
  /** Klartext zum Muster — erscheint als Hinweis, bei Verstoss als Fehler. */
  musterText?: string;
  optionen?: string[];
  /**
   * Modulschalter, an dem dieses Feld haengt.
   *
   * Fehlt er, steht das Feld immer. Ist er gesetzt und der Mandant fuehrt das
   * Modul nicht, entfaellt das Feld **ganz** — es wird nicht gezeigt, nicht
   * vorbelegt und nicht geprueft.
   *
   * Dasselbe Muster wie `Spalte.modul` in `seiten/assetkonfig.ts`. Bis zum
   * 27.08.2026 gab es das hier nicht: HOWOGE fuehrt keine Kritikalitaet, die
   * Spalte war ueberall ausgeblendet — und im Applikationsformular stand sie
   * weiter. Der Kunde hat es gemeldet.
   */
  modul?: Modul;
  /**
   * Nur sichtbar, wenn ein anderes Feld einen dieser Werte traegt.
   *
   * Seit dem 25.09.2026 fuer die technische Gruppe an der Berechtigung: es
   * gibt sie fuer Applikationen und Laufwerke, nicht fuer Postfaecher,
   * Verteiler, Hardware oder M365 — so vom Kunden festgelegt.
   */
  nurBei?: [feld: string, werte: string[]];
  /**
   * Anfangswert eines Auswahlfelds. Fehlt die Angabe, gilt wie bisher die
   * erste Option; `''` heisst „ohne Vorbelegung".
   *
   * Seit dem 30.09.2026: die Kategorie einer neuen Applikation stand still
   * auf „Standardanwendungen", weil das die erste Option war — auch wenn
   * niemand sie gewaehlt hatte.
   */
  vorgabe?: string;
  /**
   * Anfangswert abhaengig von einem anderen Feld, z. B. die Berechtigungsart
   * nach der Objektart: Postfach → „Zugriff". Aendert sich das andere Feld
   * und steht hier noch die alte Vorgabe, zieht der Wert mit.
   */
  vorgabeJe?: [feld: string, werte: Record<string, string>];
  /**
   * Optionen abhaengig von einem anderen Feld, z. B. die Berechtigungsarten
   * nach der Objektart: am Laufwerk nur „lesen" und „schreiben". Fuer Werte
   * ohne Eintrag gilt `optionen`.
   */
  optionenJe?: [feld: string, werte: Record<string, string[]>];
  /**
   * Optionen zusaetzlich aus dem Bestand (`/api/<sammlung>/_werte`) — damit
   * ein Auswahlfeld die Werte kennt, die tatsaechlich gefuehrt werden.
   */
  werteAus?: { sammlung: string; feld: string; filter?: Record<string, string> };
}

/** Die Optionen eines Auswahlfelds bei diesen Werten (`optionenJe`, sonst `optionen`). */
export function optionenFuer(fd: Feld, werte: Record<string, string>): string[] {
  if (fd.optionenJe) {
    const o = fd.optionenJe[1][werte[fd.optionenJe[0]] ?? ''];
    if (o) return o;
  }
  return fd.optionen ?? [];
}

/**
 * Welche Berechtigungsarten zu welcher Objektart passen.
 *
 * Gemeldet im produktiven Test der HOWOGE (Punkt 12, 06.10.2026): die Liste
 * war fuer jede Objektart dieselbe, und am Laufwerk liess sich „einfacher
 * Zugriff" waehlen, an der Anwendung „schreiben". Seitdem gilt:
 *
 * - Applikation: „einfacher Zugriff" und „je nach Berechtigungskonzept der
 *   Anwendung" — wie gewuenscht.
 * - Laufwerk: „lesen" und „schreiben".
 * - Postfach: „Zugriff" (in der Matrix „X") und die drei Exchange-Rechte,
 *   die am 22.09.2026 dazugekommen sind; sie stammen nicht aus der Matrix.
 * - Verteiler, Hardware, M365: nur „Zugriff".
 *
 * „Adminrecht" und „nicht beruecksichtigt" stehen nicht mehr zur Wahl: in
 * der Matrix sind sie ein Vermerk, keine Berechtigung. Gespeicherte Werte
 * bleiben, wie sie sind, und werden weiter angezeigt.
 */
export const ARTEN_JE_OBJEKTART: Record<string, string[]> = {
  Applikation: ['einfacher Zugriff', 'je nach Berechtigungskonzept der Anwendung'],
  MobileApp: ['einfacher Zugriff', 'je nach Berechtigungskonzept der Anwendung'],
  Fileserver: ['lesen', 'schreiben'],
  Postfach: ['Zugriff', 'Vollzugriff', 'Senden als', 'Senden im Auftrag'],
  Verteiler: ['Zugriff'],
  Hardware: ['Zugriff'],
  M365: ['Zugriff'],
};

/** Alle waehlbaren Berechtigungsarten — fuer Objektarten ohne eigene Liste. */
export const ARTEN_ALLE: string[] = [...new Set(Object.values(ARTEN_JE_OBJEKTART).flat())];

/** Die Vorgabe eines Felds bei diesen Werten — `undefined`, wenn es keine gibt. */
export function vorgabeFuer(fd: Feld, werte: Record<string, string>): string | undefined {
  if (fd.vorgabeJe) {
    const v = fd.vorgabeJe[1][werte[fd.vorgabeJe[0]] ?? ''];
    if (v !== undefined) return v;
  }
  return fd.vorgabe;
}

/** Ist das Feld bei diesen Werten zu zeigen? */
export function feldSichtbar(fd: Feld, werte: Record<string, string>): boolean {
  return !fd.nurBei || fd.nurBei[1].includes(werte[fd.nurBei[0]] ?? '');
}

/** Wo `nurBei` die technische Gruppe zeigt — wie `GRUPPENFAEHIG` in der Schnittstelle. */
const MIT_GRUPPE: [string, string[]] = ['assetTyp', ['Applikation', 'Fileserver']];

export interface Formular {
  kicker: string;
  titel: string;
  kurz: string;
  /** Gruppen als [Beschriftung, Felder]. */
  gruppen: [string, Feld[]][];
}

export const FORMULARE: Record<string, Formular> = {
  berechtigung: {
    kicker: 'Rollenmatrix',
    titel: 'Berechtigung anlegen',
    kurz: 'Eine Zeile der Rollenmatrix: Asset, Berechtigungsart und technische Gruppe.',
    gruppen: [
      ['Fachlich', [
        { k: 'asset', label: 'Applikation / Ressource', pflicht: true, platzhalter: 'z. B. Adobe Acrobat Pro' },
        /* Die Objektart bestimmt, welches Runbook spaeter zustaendig ist
           (`ZIELSYSTEM_SQL` in `auftraege.ts`), und sie laesst sich nach dem
           Anlegen nicht mehr aendern (`nurAnlegen`). Bis zum 27.08.2026 war sie
           hier nicht abgefragt — jede ueber dieses Formular angelegte Zeile
           bekam `AssetTyp = NULL` und damit die Zielart `unbestimmt`.

           Abgefragt und nicht aus `kategorie` abgeleitet: im Bestand steht dort
           die Unterueberschrift (AVD, Surface, iPhone), nicht der Abschnitt der
           Mappe. Eine Ableitung traefe in der Haelfte der Faelle nichts.

           Die sieben Werte sind die, die `werkzeuge/matrix-lesen.py` erzeugt. */
        { k: 'assetTyp', label: 'Objektart', typ: 'auswahl', pflicht: true,
          optionen: ['Applikation', 'Fileserver', 'Postfach', 'Verteiler',
            'M365', 'MobileApp', 'Hardware'] },
        { k: 'kategorie', label: 'Kategorie', typ: 'auswahl', optionen: ['Standardanwendungen', 'Weitere Anwendungen', 'Apps für mobile Endgeräte', 'Laufwerkzugriffe', 'Funktionspostfächer', 'Email-Verteiler', 'MS Teams-Gruppemzuordnung', 'Hardware-Anforderungen'] },
        { k: 'rolleId', label: 'Rolle', pflicht: true, platzhalter: 'IT-IP-01', muster: '^[A-Za-z]{1,4}-[A-Za-z][A-Za-z ]{0,6}-\\d{1,2}$', musterText: 'Muster OE-ABT-NN, z. B. IT-IP-01' },
        /* Die Liste fuehrt seit dem 22.09.2026 auch die Arten, die an
           Postfaechern, Verteilern und Ausstattung stehen.

           Zwei Gruende, beide am Kundenbestand nachgezaehlt:

           - **„Zugriff" fehlte**, obwohl 1.572 Zuordnungen ihn tragen
             (792 Postfaecher, 465 Verteiler, 287 Ausstattung, 28 M365). Im
             Rolleneditor stand deshalb an jeder dieser Zeilen ein
             Auswahlfeld ohne getroffene Wahl — der Wert war da, die Liste
             kannte ihn nur nicht.
           - **Die Zugriffsart eines Postfachs gehoert hierher**, an die
             Zuordnung, nicht an das Postfach. Der Kunde am 22.09.2026:
             „wenn man jetzt auf so ein Funktionspostfach geht, dann ist ja
             da die Zugriffsart und das sollte ja eigentlich eher dann bei
             der Rolle stehen." Er hat recht: ob jemand Vollzugriff hat oder
             im Auftrag sendet, ist eine Eigenschaft der Zuordnung — dasselbe
             Postfach wird von verschiedenen Rollen verschieden genutzt.

           **Die Quelle kennt diese Unterscheidung nicht.** In der
           Standardrollenmatrix steht an jedem Postfach schlicht „Zugriff";
           Vollzugriff, Senden als und Senden im Auftrag lassen sich daraus
           nicht ableiten und werden deshalb auch nicht geraten. Sie stehen
           ab jetzt zur Wahl, und wer es weiss, traegt es je Zuordnung nach. */
        { k: 'berechtigungsart', label: 'Berechtigungsart', typ: 'auswahl', pflicht: true,
          /* „X" aus der Matrix heisst „Zugriff" — an Postfach, Verteiler,
             Hardware und M365 die Vorgabe (30.09.2026). */
          vorgabeJe: ['assetTyp', { Applikation: 'einfacher Zugriff', Fileserver: 'lesen',
            Postfach: 'Zugriff', Verteiler: 'Zugriff', Hardware: 'Zugriff', M365: 'Zugriff' }],
          /* Je Objektart nur die passenden Arten (06.10.2026) — siehe
             `ARTEN_JE_OBJEKTART`. */
          optionenJe: ['assetTyp', ARTEN_JE_OBJEKTART],
          optionen: ARTEN_ALLE },
      ]],
      ['Technisch', [
        /* Seit dem 25.09.2026 **kein Pflichtfeld und kein Namensschema**.
         *
         * Bis dahin verlangte das Formular an jeder Zeile einen Gruppennamen
         * der Form `IAM_{IDP}_{Objektklasse}_…` — auch an einem Postfach,
         * auch dort, wo noch keine Gruppe feststeht. Eine Zeile liess sich
         * damit nicht aendern, ohne einen Namen zu erfinden; genau das will
         * der Kunde nicht. „Noch nicht hinterlegt" ist ein zulaessiger
         * Zustand, und die Gruppe wird in der Regel je Objekt und
         * Zugriffsart gepflegt (Rollenmatrix → „Technische Gruppe
         * hinterlegen"). Hier steht sie fuer den Einzelfall.
         *
         * Die Group ID hatte ein Muster aus drei Bloecken zu vier Zeichen.
         * Eine Entra-Objektkennung ist eine GUID; das Muster haette jede
         * echte abgewiesen. */
        { k: 'gruppenName', label: 'Gruppen-Name', breit: true, modul: 'gruppenmapping', nurBei: MIT_GRUPPE,
          platzhalter: 'noch nicht hinterlegt' },
        { k: 'groupId', label: 'Group ID', modul: 'gruppenmapping', nurBei: MIT_GRUPPE },
        { k: 'idp', label: 'IDP', typ: 'auswahl', modul: 'gruppenmapping', nurBei: MIT_GRUPPE, optionen: ['Entra', 'AD', 'SAP', 'lokal'] },
        { k: 'gruppentyp', label: 'Gruppentyp', typ: 'auswahl', modul: 'gruppenmapping', nurBei: MIT_GRUPPE, optionen: ['Sicherheitsgruppe', 'M365-Gruppe', 'Verteiler', 'AD-Gruppe'] },
        { k: 'provisionierung', label: 'Provisionierung', typ: 'auswahl', optionen: ['automatisch', 'manuell', 'Ticket'] },
      ]],
      ['Ergänzung', [
        { k: 'zusatz', label: 'Zusatz', breit: true, platzhalter: 'Mail;finance@beispiel.invalid;PDF' },
        { k: 'status', label: 'Status', typ: 'auswahl', optionen: ['aktiv', 'beantragt', 'entwertet'] },
      ]],
    ],
  },

  applikation: {
    kicker: 'IT-Assets',
    titel: 'Applikation erfassen',
    kurz: 'Neue Anwendung mit Owner, Lizenzmodell und Berechtigungskonzept.',
    gruppen: [
      ['Stammdaten', [
        /* Das Logofeld ist am 03.09.2026 entfallen. Es hat nie etwas
           gespeichert: `api/anlegen.ts` fuehrt `logo` unter den Feldern, die
           ausdruecklich **nicht** in die Fachtabelle gehen („Bilder gehoeren
           nicht in die Fachtabelle; dafuer fehlt die Ablage."). Ein Feld, das
           ein Bild annimmt und wegwirft, ist die Anti-Regel dieses Projekts —
           der Kunde hat es gemeldet: „das brauchen wir nicht mehr."

           Mit dem Feld sind der Typ `logo` und die Komponente `Logofeld` in
           `bausteine/Drawer.tsx` entfallen — kein Formular hat sie mehr
           benutzt, und toter Code ist in diesem Projekt ein eigener Befund. */
        { k: 'name', label: 'Name', pflicht: true, platzhalter: 'z. B. Scompler' },
        { k: 'hersteller', label: 'Hersteller', platzhalter: 'z. B. Scompler GmbH' },
        /* Die Kategorie kennt, was der Bestand fuehrt (AVD, Surface,
           Web-Anwendungen, Weitere Anwendungen, iPhone …) — die feste Liste
           kannte davon nur einen Teil, und ein gespeichertes „AVD" stand im
           Formular als „Standardanwendungen" da (30.09.2026). Ohne
           Vorbelegung: gewaehlt wird sie, nicht geerbt. */
        { k: 'kategorie', label: 'Kategorie', typ: 'auswahl', vorgabe: '',
          /* iPhone und iPad seit dem 07.10.2026 (HOWOGE, Punkt 18): mobile
             Apps stehen bei den Applikationen und lassen sich hier anlegen.
             Die Kategorie entscheidet die Objektart — `ableiten` in
             `api/anlegen.ts` macht daraus `MobileApp`, wie der Import. */
          optionen: ['Standardanwendungen', 'Weitere Anwendungen', 'iPhone', 'iPad'],
          werteAus: { sammlung: 'assets', feld: 'kategorie', filter: { assetTyp: 'Applikation,MobileApp' } } },
        { k: 'typ', label: 'Typ', typ: 'auswahl', optionen: ['Entra App', 'Intune App', 'AVD', 'Web', 'Client', 'SAP'] },
        { k: 'kritikalitaet', label: 'Kritikalität', typ: 'auswahl', modul: 'kritikalitaet',
          optionen: ['gering', 'mittel', 'hoch', 'sehr hoch'] },
      ]],
      ['Verantwortung', [
        /* Zwei Verantwortungen, seit dem 26.08.2026 getrennt.
         *
         * Der Kunde hat es am 24.08.2026 als Show-Stopper gemeldet: „Die
         * Applikationsverantwortung ist nicht rollenbasiert und muss sich auf
         * Anwendungen bzw. Services beziehen" — und dazu festgelegt: „in
         * Applikation: Fachlicher & Technischer Owner".
         *
         * `iam.Asset` fuehrt beide Spalten seit `001` (`FachlicherOwner` und
         * `OwnerName`). Die zweite war bis heute nur ueber das Teamformular
         * erreichbar, wo sie „Owner 2" hiess; an Applikationen gab es genau
         * ein Feld „Owner", und welche der beiden Verantwortungen es meinte,
         * stand nirgends.
         *
         * **Getauscht wird nichts.** Am Kundenbestand nachgesehen sind beide
         * Spalten durchgehend leer (`matrix-lesen.py` legt sie ausdruecklich
         * leer an) — es gibt also keine Werte, die durch die Benennung eine
         * falsche Bedeutung bekaemen. */
        { k: 'ownerFachlich', label: 'Fachlicher Owner', pflicht: true, platzhalter: 'Vorname Nachname' },
        /* Beide Pflicht, und je eine Adresse dazu — der Kunde am 27.08.2026:
         * „beides als Pflichtfeld und sowohl fachlich als auch technischer
         * Owner mit Email."
         *
         * Bis dahin war der technische Owner freiwillig und es gab **eine**
         * Adresse fuer beide, ohne dass an ihr stand, wem sie gehoert. Genau
         * die Unklarheit war der Grund, die Namen am 26.08.2026 zu trennen;
         * eine gemeinsame Adresse haette sie durch die Hintertuer
         * zurueckgebracht.
         *
         * Die Adressspalten stehen seit `045`. Vorher wurde `ownerMail`
         * eingegeben und beim Anlegen verworfen — ein Feld, das nichts tat. */
        { k: 'ownerFachlichMail', label: 'E-Mail fachlicher Owner', pflicht: true, muster: '^[^@\\s]+@[^@\\s]+\\.[A-Za-z]{2,}$', musterText: 'Gültige E-Mail-Adresse angeben', platzhalter: 'vorname.nachname@beispiel.invalid' },
        { k: 'owner', label: 'Technischer Owner', pflicht: true, platzhalter: 'Vorname Nachname' },
        { k: 'ownerMail', label: 'E-Mail technischer Owner', pflicht: true, muster: '^[^@\\s]+@[^@\\s]+\\.[A-Za-z]{2,}$', musterText: 'Gültige E-Mail-Adresse angeben', platzhalter: 'vorname.nachname@beispiel.invalid' },
        /* Drei Werte statt der drei Saetze aus der Mappe.
           Die Mappe fuehrt als Auswahl „ja" / „nein, ich brauche Hilfe bei
           der Erstellung" / „nein, es ist keins notwendig" — Saetze, die in
           einer Tabellenspalte umbrechen und sich nicht filtern lassen. Der
           Kunde hat am 24.08.2026 „ja / nein / Klaerung" verlangt: „nein"
           heisst, es braucht keines, „Klaerung" heisst, es fehlt eines. */
        { k: 'konzept', label: 'Berechtigungskonzept', typ: 'auswahl', optionen: ['ja', 'nein', 'Klärung'], breit: true },
      ]],
      ['Lizenz', [
        { k: 'kosten', label: 'Netto-Lizenzkosten je Nutzer und Jahr', muster: '^[0-9]{1,6}([.,][0-9]{1,2})?$', musterText: 'Nur Zahl, z. B. 134,23', platzhalter: '134,23' },
        /* Ohne diese Zahl gibt es keine Jahreskosten — die Mappe des Kunden
           fuehrt sie nicht, und die Lizenzseite sagt das seit dem 25.08.2026
           ausdruecklich, statt „0,00 €“ zu zeigen. */
        { k: 'lizenzmodell', label: 'Lizenzmodell', typ: 'auswahl', optionen: ['Benutzer-Lizenz', 'Gerätelizenz', 'Concurrent', 'kostenfrei', 'in anderer Position enthalten'] },
        { k: 'lizenzhinweis', label: 'Hinweis aus der Preisliste', breit: true, platzhalter: 'z. B. in SAP ERP enthalten' },
      ]],
    ],
  },

  gruppe: {
    kicker: 'IT-Assets',
    titel: 'Technische Gruppe anlegen',
    kurz: 'Entra- oder AD-Gruppe erfassen — mit dem Namen, den sie im Verzeichnis trägt.',
    gruppen: [
      ['Identität', [
        /* Kein Namensschema mehr (25.09.2026). Das Muster `IAM_{IDP}_…` war
         * eine Konvention der Vorlage, nicht die des Kunden: eine Gruppe, die
         * im Verzeichnis anders heisst, liess sich nicht erfassen — man
         * haette ihren Namen verfaelschen muessen. Die Group ID ist eine
         * GUID oder eine AD-Kennung, keine drei Bloecke zu vier Zeichen;
         * sie ist optional, weil sie beim Erfassen oft noch nicht vorliegt.
         * Dieselben Angaben wie der Anlegeweg in der Rollenmatrix
         * (`bausteine/Gruppenweg.tsx`). */
        { k: 'name', label: 'Gruppenname', pflicht: true, breit: true, platzhalter: 'Name wie im Verzeichnis' },
        { k: 'groupId', label: 'Group ID' },
        { k: 'idp', label: 'IDP', typ: 'auswahl', optionen: ['Entra', 'AD'] },
      ]],
      ['Eigenschaften', [
        { k: 'gruppentyp', label: 'Gruppentyp', typ: 'auswahl', optionen: ['Sicherheitsgruppe', 'M365-Gruppe', 'Verteiler', 'Mail-aktiviert'] },
        { k: 'zielsystem', label: 'Zielsystem', platzhalter: 'z. B. Adobe Cloud' },
        { k: 'owner', label: 'Owner', platzhalter: 'Vorname Nachname' },
        { k: 'beschreibung', label: 'Beschreibung', typ: 'fliess', breit: true, platzhalter: 'Wofür wird diese Gruppe verwendet?' },
      ]],
    ],
  },

  laufwerk: {
    kicker: 'IT-Assets',
    titel: 'Laufwerksberechtigung anlegen',
    kurz: 'Pfad und Verantwortung erfassen.',
    gruppen: [
      ['Pfad', [
        { k: 'pfad', label: 'Pfad', pflicht: true, breit: true, platzhalter: 'A:\\Informationstechnologie\\Infrastruktur',
          muster: '^([A-Za-z]:|\\\\\\\\)', musterText: 'Laufwerksbuchstabe oder UNC-Pfad' },
        /* **„Bezeichnung" ist entfallen.**
         *
         * Das Feld schrieb `iam.Asset.Kategorie`, und dort steht bei einem
         * Laufwerk immer derselbe Wert: am Bestand nachgezaehlt tragen
         * **458 von 458** Fileservern „Laufwerkzugriffe". Genau deshalb ist
         * die Spalte in Runde 7 aus der Liste geflogen — sie sagte in jeder
         * Zeile dasselbe.
         *
         * Als **Pflichtfeld** stand sie trotzdem weiter da: wer einen neuen
         * Pfad anlegte, musste einen Wert eintippen, den das System selbst
         * kennt, und konnte dabei einen abweichenden erfinden. Eine
         * Pflichtangabe ohne Wahl ist keine Angabe, sondern eine Huerde.
         *
         * Der Wert wird jetzt gesetzt (`feste.kategorie` in
         * `api/anlegen.ts`). Beim **Bearbeiten** bleibt die Spalte
         * unangetastet: `werteFuerAenderung` liest nur `felder`, nicht
         * `feste`. */
        /* **„Zugriff" ist entfallen** (22.09.2026).
         *
         * Das Feld stand im Formular, wurde aber nie gespeichert: es gibt
         * keine Spalte dafuer, und es gaebe auch keine richtige. Die
         * Zugriffsart haengt an der **Zuordnung** — dasselbe Laufwerk wird
         * von einer Rolle gelesen und von der naechsten beschrieben. Sie
         * steht im Rolleneditor als Berechtigungsart zur Wahl.
         *
         * Ein Feld, das eine Eingabe entgegennimmt und verwirft, ist
         * schlimmer als keins: es sieht aus wie eine Zusage. */
      ]],
      ['Technik', [
        /* **„AD-Gruppe" ist entfallen** (22.09.2026).
         *
         * Auch dieses Feld wurde nie gespeichert. Die technische Gruppe ist
         * ein eigener Datensatz (`iam.Gruppe`) und wird mit der Berechtigung
         * **verknuepft**, nicht am Laufwerk abgeschrieben; zwei Namen
         * nebeneinander waeren zwei Wahrheiten. Der Kunde am 22.09.2026:
         * „zusaetzlich steht bei File-Server noch AD-Gruppe drin bei
         * Technik, dass man das ausfuellen kann. Das wollen wir natuerlich
         * nicht." Bei der HOWOGE kommt hinzu, dass der Mandant ueberhaupt
         * keine Gruppenzuordnung fuehrt (`module.gruppenmapping`). */
        { k: 'owner', label: 'Owner', platzhalter: 'Vorname Nachname' },
        { k: 'ownerMail', label: 'E-Mail Owner', muster: '^[^@\s]+@[^@\s]+\.[A-Za-z]{2,}$', musterText: 'Gültige E-Mail-Adresse angeben', platzhalter: 'vorname.nachname@beispiel.invalid' },
        { k: 'vererbung', label: 'Vererbung', typ: 'auswahl', optionen: ['eigen', 'geerbt'] },
      ]],
    ],
  },

  postfach: {
    kicker: 'IT-Assets',
    titel: 'Postfach anlegen',
    kurz: 'Funktionspostfach oder Verteiler mit Typ und Owner.',
    gruppen: [
      ['Adresse', [
        { k: 'adresse', label: 'Adresse', pflicht: true, muster: '^[^@\\s]+@[^@\\s]+\\.[A-Za-z]{2,}$', musterText: 'Gültige E-Mail-Adresse angeben', platzhalter: 'serviceteam@beispiel.invalid' },
        { k: 'anzeigename', label: 'Anzeigename', pflicht: true, platzhalter: 'Serviceteam' },
        { k: 'typ', label: 'Typ', typ: 'auswahl', optionen: ['Funktionspostfach', 'Sammelpostfach', 'Raum', 'Ressource', 'Verteilerliste', 'M365-Gruppe', 'dynamisch'] },
        /* **„Zugriffsart" ist entfallen** (22.09.2026) — sie steht jetzt an
         * der Zuordnung. Ein Postfach **hat** keine Zugriffsart; es hat so
         * viele, wie es Rollen darauf gibt. Am Postfach erfasst galt sie fuer
         * alle gleichermassen, und gespeichert wurde sie ohnehin nie. Die
         * Werte (Vollzugriff, Senden als, Senden im Auftrag) stehen im
         * Rolleneditor als Berechtigungsart zur Wahl. */
      ]],
      ['Verwaltung', [
        { k: 'owner', label: 'Owner', pflicht: true, platzhalter: 'Vorname Nachname' },
        { k: 'ownerMail', label: 'E-Mail Owner', muster: '^[^@\s]+@[^@\s]+\.[A-Za-z]{2,}$', musterText: 'Gültige E-Mail-Adresse angeben', platzhalter: 'vorname.nachname@beispiel.invalid' },
        { k: 'aufbewahrung', label: 'Aufbewahrung', typ: 'auswahl', optionen: ['Standard 10 Jahre', '6 Jahre', 'unbefristet'] },
        { k: 'extern', label: 'Extern zustellbar', typ: 'auswahl', optionen: ['nein', 'ja'] },
      ]],
    ],
  },

  team: {
    kicker: 'IT-Assets',
    titel: 'Team anlegen',
    kurz: 'Team, M365-Gruppe oder SharePoint-Site mit mindestens zwei Ownern.',
    gruppen: [
      ['Stammdaten', [
        { k: 'name', label: 'Name', pflicht: true, platzhalter: 'IT Applikationsmanagement' },
        { k: 'typ', label: 'Typ', typ: 'auswahl', optionen: ['Team', 'M365-Gruppe', 'SharePoint-Site'] },
        { k: 'url', label: 'Pfad / URL', breit: true, platzhalter: 'https://beispiel.sharepoint.com/sites/…' },
      ]],
      ['Verantwortung', [
        /* Dieselben zwei Verantwortungen wie an der Applikation — bis zum
           26.08.2026 hiessen sie hier „Owner 1" und „Owner 2" und landeten in
           denselben zwei Spalten, ohne dass jemand haette sagen koennen,
           welche welche ist. */
        /* **Ein** Owner, nicht zwei. Am 01.09.2026 abbestellt: an einem
           Team oder einer M365-Gruppe gibt es beim Kunden eine
           Verantwortung, keine zwei. `FachlicherOwner` bleibt im Bestand
           stehen und wird nur nicht mehr abgefragt — was dort steht,
           bleibt stehen. */
        { k: 'owner', label: 'Owner', pflicht: true, platzhalter: 'Vorname Nachname' },
        { k: 'ownerMail', label: 'E-Mail Owner', muster: '^[^@\s]+@[^@\s]+\.[A-Za-z]{2,}$', musterText: 'Gültige E-Mail-Adresse angeben', platzhalter: 'vorname.nachname@beispiel.invalid' },
        /* Beschriftung „Vertraulichkeit", Werte wie ueberall sonst — und
           seit dem 27.08.2026 landet das Feld auch irgendwo.
           Bis dahin hiess es `vertraulichkeit`, hatte einen eigenen
           Wertevorrat und wurde beim Anlegen **verworfen**
           (`api/anlegen.ts`: „Keine Spalte im Schema"). Die Spalte an M365
           heisst zwar „Vertraulichkeit", zeigt aber `iam.Asset.Kritikalitaet`
           — das Formular schrieb also in ein Nichts, waehrend daneben eine
           Spalte darauf wartete. Zwei Vokabulare in einer Spalte waeren die
           schlechtere Loesung gewesen. */
        { k: 'kritikalitaet', label: 'Vertraulichkeit', typ: 'auswahl', modul: 'kritikalitaet',
          optionen: ['gering', 'mittel', 'hoch', 'sehr hoch'] },
        { k: 'gaeste', label: 'Gäste zulassen', typ: 'auswahl', optionen: ['nein', 'ja'] },
      ]],
    ],
  },

  /* Ein Hardwareeintrag ist beim Kunden ein **Ausstattungspaket** — ein
     Warenkorb, den eine Rolle bekommt. 31 solcher Pakete stehen im Bestand,
     350 Zuordnungen haengen daran, und 26 tragen einen Link auf die
     SharePoint-Liste. Der Link liess sich bis zum 25.08.2026 weder eintragen
     noch aendern: das Formular kannte kein Feld dafuer, und `ABBILDUNGEN`
     kannte `verweis` nicht — womit ihn auch das Bearbeiten verwarf. */
  hardware: {
    kicker: 'IT-Assets',
    titel: 'Hardware-Eintrag anlegen',
    kurz: 'Gerät oder Ausstattungspaket samt Warenkorb.',
    gruppen: [
      ['Gerät', [
        { k: 'geraeteklasse', label: 'Geräteklasse', typ: 'auswahl', pflicht: true, optionen: ['Notebook', 'Tablet', 'Smartphone', 'Virtueller Desktop', 'Peripherie', 'Ausstattungspaket', 'Sonstiges'] },
        { k: 'bezeichnung', label: 'Bezeichnung', pflicht: true, platzhalter: 'Surface Laptop 6' },
        { k: 'standard', label: 'Standardausstattung', typ: 'auswahl', optionen: ['ja', 'nein'] },
        { k: 'kosten', label: 'Beschaffungskosten', muster: '^[0-9]{1,7}([.,][0-9]{1,2})?$', musterText: 'Nur Zahl, z. B. 1450,00', platzhalter: '1450,00' },
      ]],
      ['Warenkorb', [
        { k: 'verweis', label: 'Warenkorb / Verweis', typ: 'fliess', breit: true, platzhalter: 'https://… — eine vollständige Adresse ist anklickbar. Ein relativer Pfad aus der Rollenmatrix wird erst aufrufbar, wenn die Basisadresse der SharePoint-Bibliothek feststeht.' },
      ]],
      ['Verantwortung', [
        { k: 'owner', label: 'Owner', platzhalter: 'Vorname Nachname' },
        { k: 'ownerMail', label: 'E-Mail Owner', muster: '^[^@\s]+@[^@\s]+\.[A-Za-z]{2,}$', musterText: 'Gültige E-Mail-Adresse angeben', platzhalter: 'vorname.nachname@beispiel.invalid' },
      ]],
      ['Zuordnung', [
        { k: 'begruendung', label: 'Begründung bei Abweichung', typ: 'fliess', breit: true, platzhalter: 'Pflicht, sobald die Ausstattung vom Standard abweicht.' },
      ]],
    ],
  },

  /* Eigenes Formular, weil die Objektart eine andere ist — siehe
     `formularJeAsset`. Bewusst schlank: eine mobile Anwendung hat weder
     Geraeteklasse noch Beschaffungskosten. */
  mobileapp: {
    kicker: 'IT-Assets',
    titel: 'Mobile Anwendung erfassen',
    kurz: 'Freigegebene App für dienstliche Endgeräte.',
    gruppen: [
      ['Anwendung', [
        { k: 'bezeichnung', label: 'Bezeichnung', pflicht: true, platzhalter: 'z. B. Microsoft Authenticator' },
        { k: 'kategorie', label: 'Geräteklasse', typ: 'auswahl', optionen: ['iPhone', 'iPad', 'Android', 'geräteunabhängig'] },
        { k: 'kosten', label: 'Kosten je Nutzer und Jahr', muster: '^[0-9]{1,7}([.,][0-9]{1,2})?$', musterText: 'Nur Zahl, z. B. 12,00', platzhalter: '0,00' },
      ]],
      ['Warenkorb', [
        { k: 'verweis', label: 'Warenkorb / Verweis', typ: 'fliess', breit: true, platzhalter: 'https://…' },
      ]],
      ['Verantwortung', [
        { k: 'owner', label: 'Owner', platzhalter: 'Vorname Nachname' },
        { k: 'ownerMail', label: 'E-Mail Owner', muster: '^[^@\s]+@[^@\s]+\.[A-Za-z]{2,}$', musterText: 'Gültige E-Mail-Adresse angeben', platzhalter: 'vorname.nachname@beispiel.invalid' },
      ]],
    ],
  },

  punkt: {
    kicker: 'Governance',
    titel: 'Offenen Punkt anlegen',
    kurz: 'Klärungsbedarf mit Organisationseinheit, Verantwortlichem und Fälligkeit.',
    gruppen: [
      ['Sache', [
        { k: 'thema', label: 'Thema', pflicht: true, breit: true, platzhalter: 'Was ist zu klären?' },
        { k: 'oeCode', label: 'Organisationseinheit', typ: 'auswahl', pflicht: true, optionen: [] },
        { k: 'status', label: 'Status', typ: 'auswahl', optionen: ['offen', 'in Bearbeitung', 'zurückgestellt', 'erledigt', 'Info'] },
      ]],
      ['Herkunft', [
        /* Wer den Punkt gemeldet hat — Freitext. Ein Klaerungspunkt kommt
           regelmaessig von jemandem ohne Datensatz in diesem Werkzeug. Nicht
           zu verwechseln mit „Verantwortlich": das ist, wer ihn bearbeitet. */
        { k: 'angefordertVon', label: 'Angefordert von', platzhalter: 'Name, Bereich oder Gremium' },
        /* Freiwillig, und hier ihre einzige Quelle: es gibt keine Anbindung an
           ein Ticketsystem. Bleibt sie leer, steht in der Liste ein Strich. */
        { k: 'ticket', label: 'Ticketnummer', muster: '^.{0,60}$', musterText: 'Höchstens 60 Zeichen', platzhalter: 'z. B. INC0012345' },
      ]],
      ['Bearbeitung', [
        { k: 'verantwortlich', label: 'Verantwortlich', platzhalter: 'Vorname Nachname' },
        { k: 'faelligkeit', label: 'Fälligkeit', muster: '^\\d{2}\\.\\d{2}\\.\\d{4}$', musterText: 'Format TT.MM.JJJJ', platzhalter: '30.09.2026' },
        { k: 'bemerkung', label: 'Bemerkung', typ: 'fliess', breit: true, platzhalter: 'Stand, Absprachen, nächste Schritte' },
      ]],
    ],
  },
};

/** Welches Formular gehoert zu welcher Asset-Seite (`FORM_JE_ASSET`). */
export function formularJeAsset(pfad: string, bereich = ''): string {
  if (pfad === '/assets/applikationen') return 'applikation';
  if (pfad === '/assets/fileserver') return 'laufwerk';
  if (pfad === '/assets/exchange') return 'postfach';
  if (pfad === '/assets/m365') return 'team';
  /* Zwei Reiter, zwei Objektarten. Bis zum 25.08.2026 benutzten beide dasselbe
     Formular, und das setzt fest `assetTyp: 'Hardware'` — ein im Reiter
     „Mobile Apps" angelegter Eintrag landete als Hardware und tauchte im
     eigenen Reiter nie wieder auf. */
  if (pfad === '/assets/hardware') return bereich === 'mobile' ? 'mobileapp' : 'hardware';
  return 'gruppe';
}

/**
 * Das Formular, wie dieser Mandant es sieht.
 *
 * Felder an einem abgeschalteten Modul entfallen, und eine Gruppe, die
 * dadurch leer wird, entfaellt mit — eine Ueberschrift ueber nichts ist
 * schlimmer als eine fehlende.
 *
 * `!== false` und nicht `=== true`: ein Modul, das ein Profil gar nicht
 * nennt, blendet nichts aus. Dieselbe Konvention wie in `Assets.tsx`.
 *
 * **Wichtig fuer Vorbelegung und Pruefung:** beide arbeiten auf dem Ergebnis
 * dieser Funktion, nicht auf `FORMULARE`. Sonst bekaeme ein ausgeblendetes
 * Pflichtfeld eine Vorbelegung, die niemand sieht — oder schlimmer, es
 * blockierte das Speichern, ohne dass irgendwo stuende, warum.
 */
export function formularFuerModule(
  kat: Formular,
  module: Partial<Record<Modul, boolean>>,
): Formular {
  const gruppen = kat.gruppen
    .map(([label, felder]): [string, Feld[]] =>
      [label, felder.filter((fd) => !fd.modul || module[fd.modul] !== false)])
    .filter(([, felder]) => felder.length > 0);
  return { ...kat, gruppen };
}

/** Pruefung eines einzelnen Feldes (`formFeldFehler`). */
export function feldFehler(fd: Feld, wert: string | undefined): string | null {
  const v = (wert || '').trim();
  if (fd.pflicht && !v) return 'Pflichtfeld — bitte ausfüllen.';
  if (v && fd.muster && !new RegExp(fd.muster).test(v)) return fd.musterText || 'Format prüfen.';
  return null;
}

/** Vorbelegung beim Oeffnen (`formOeffnen`): Auswahlfelder auf die erste Option. */
export function anfangswerte(
  formular: Formular,
  vorbelegung?: Record<string, string> | null,
): Record<string, string> {
  const werte: Record<string, string> = {};
  formular.gruppen.forEach(([, felder]) =>
    felder.forEach((fd) => {
      const vor = vorbelegung?.[fd.k];
      const vorgabe = vorgabeFuer(fd, werte);
      werte[fd.k] =
        vor != null ? vor
          : fd.typ === 'auswahl' ? (vorgabe !== undefined ? vorgabe : (optionenFuer(fd, werte)[0] ?? '')) : '';
    }),
  );
  return werte;
}
